import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { Textarea } from 'primeng/textarea';
import {
  ACTION_TYPE_META,
  ActionType,
  formatStamp,
} from '../../models/activity.model';
import {
  ActivityInboxService,
  describeHttpError,
  RtlPolicy,
} from '../../services/activity-inbox.service';

/** Policy values Jena can set. `auto_deny` drops that action type at create
 * time rather than auto-running it or sending it to Review. */
export type PolicyDisposition = 'needs_approval' | 'auto_execute' | 'auto_deny';

export interface PolicyAdminRow {
  actionType: string;
  disposition: PolicyDisposition;
  description: string;
}

type DispositionFilterValue = '' | PolicyDisposition;

/**
 * Administration table for the RTL action-type policy. Same shell as Ken's
 * suggestion-chips admin: filter, refresh, PrimeNG table, edit dialog.
 * There is no create/delete — the rows are Jena's starting Care Plan types
 * plus `undefined` as the clamp bucket.
 */
@Component({
  selector: 'bn-rtl-policy-admin',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    Button,
    Dialog,
    Select,
    TableModule,
    Textarea,
  ],
  templateUrl: './rtl-policy-admin.component.html',
  styleUrl: './rtl-policy-admin.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RtlPolicyAdminComponent {
  private readonly _inbox = inject(ActivityInboxService);
  private readonly _fb = inject(FormBuilder);
  private readonly _messages = inject(MessageService);

  readonly dispositionFilterOptions: { label: string; value: DispositionFilterValue }[] =
    [
      { label: 'All dispositions', value: '' },
      { label: 'needs_approval', value: 'needs_approval' },
      { label: 'auto_execute', value: 'auto_execute' },
      { label: 'auto_deny', value: 'auto_deny' },
    ];

  readonly dispositionEditOptions: { label: string; value: PolicyDisposition }[] = [
    { label: 'needs_approval', value: 'needs_approval' },
    { label: 'auto_execute', value: 'auto_execute' },
    { label: 'auto_deny', value: 'auto_deny' },
  ];

  readonly rows = signal<PolicyAdminRow[]>([]);
  readonly dispositionFilter = signal<DispositionFilterValue>('');
  readonly updatedAt = signal<string | null>(null);
  readonly updatedBy = signal<string | null>(null);
  readonly updatedAtLabel = computed(() => formatStamp(this.updatedAt()));
  readonly tableRowsPerPage = 10;
  readonly loading = signal(false);
  readonly dialogOpen = signal(false);
  readonly saving = signal(false);
  private readonly _editingRow = signal<PolicyAdminRow | null>(null);

  readonly visibleRows = computed(() => {
    const filter = this.dispositionFilter();
    const rows = this.rows();
    return filter ? rows.filter((row) => row.disposition === filter) : rows;
  });

  readonly form = this._fb.nonNullable.group({
    actionType: [''],
    disposition: ['needs_approval' as PolicyDisposition, [Validators.required]],
    description: [''],
  });

  constructor() {
    this.reload();
  }

  onDispositionFilterChange(value: DispositionFilterValue): void {
    this.dispositionFilter.set(value);
  }

  openEdit(row: PolicyAdminRow): void {
    this._editingRow.set(row);
    this.form.reset({
      actionType: row.actionType,
      disposition: row.disposition,
      description: row.description,
    });
    this.dialogOpen.set(true);
  }

  closeDialog(): void {
    this.dialogOpen.set(false);
    this._editingRow.set(null);
  }

  dispositionOptionsFor(actionType: string): { label: string; value: PolicyDisposition }[] {
    if (actionType === 'undefined') {
      return this.dispositionEditOptions.filter((opt) => opt.value !== 'auto_execute');
    }
    return this.dispositionEditOptions;
  }

  async save(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    if (raw.actionType === 'undefined' && raw.disposition === 'auto_execute') {
      this._messages.add({
        severity: 'error',
        summary: 'Save failed',
        detail: "action_type 'undefined' cannot be auto_execute; it has no executor",
        life: 6000,
      });
      return;
    }
    this.saving.set(true);
    try {
      // Reload before PUT so this row overlays the latest document instead of
      // reconstructing the whole policy from the initially loaded in-memory rows.
      const latest = await this._inbox.loadPolicy();
      const dispositions = { ...(latest.dispositions ?? {}) };
      const descriptions = { ...(latest.descriptions ?? {}) };
      dispositions[raw.actionType] = raw.disposition;
      descriptions[raw.actionType] = raw.description;
      const saved = await this._inbox.savePolicy({
        dispositions,
        descriptions,
        expected_updated_at: latest.updated_at,
      });
      this.applyPolicy(saved);
      this._messages.add({ severity: 'success', summary: 'Saved', life: 2500 });
      this.closeDialog();
    } catch (err) {
      if ((err as { status?: number }).status === 409) {
        this._messages.add({
          severity: 'warn',
          summary: 'Policy changed',
          detail:
            'Another update landed first. Reloaded the latest policy — review your change and try again.',
          life: 8000,
        });
        await this.reload();
        return;
      }
      this._messages.add({
        severity: 'error',
        summary: 'Save failed',
        detail: describeHttpError(err),
        life: 6000,
      });
    } finally {
      this.saving.set(false);
    }
  }

  async reload(): Promise<void> {
    this.loading.set(true);
    try {
      this.applyPolicy(await this._inbox.loadPolicy());
    } catch (err) {
      this._messages.add({
        severity: 'error',
        summary: 'Could not load policy',
        detail: describeHttpError(err),
        life: 6000,
      });
    } finally {
      this.loading.set(false);
    }
  }

  descriptionPreview(description: string): string {
    const max = 80;
    return description.length <= max ? description : `${description.slice(0, max)}…`;
  }

  private applyPolicy(policy: RtlPolicy): void {
    this.rows.set(rowsFromPolicy(policy));
    this.updatedAt.set(policy.updated_at);
    this.updatedBy.set(policy.updated_by ?? null);
  }
}

export function rowsFromPolicy(policy: RtlPolicy): PolicyAdminRow[] {
  const known = Object.keys(ACTION_TYPE_META);
  const extras = Object.keys(policy.dispositions ?? {}).filter(
    (key) => !(key in ACTION_TYPE_META),
  );
  return [...known, ...extras].map((actionType) => ({
    actionType,
    disposition: asPolicyDisposition(policy.dispositions?.[actionType]),
    description:
      policy.descriptions?.[actionType] ??
      ACTION_TYPE_META[actionType as ActionType]?.description ??
      '',
  }));
}

function asPolicyDisposition(value: string | undefined): PolicyDisposition {
  if (value === 'auto_execute' || value === 'auto_deny') {
    return value;
  }
  return 'needs_approval';
}
